import React, { useState } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import '../styles/Assistant.css';

const Assistant = ({ isOpen, onClose, isFullPage }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('gettingStarted');
  const [showTip, setShowTip] = useState(false);
  const [currentTip, setCurrentTip] = useState(0);

  // Build help topics from translations
  const HELP_TOPICS = {
    gettingStarted: [
      { q: t('assistant.gettingStarted.q1'), a: t('assistant.gettingStarted.a1') },
      { q: t('assistant.gettingStarted.q2'), a: t('assistant.gettingStarted.a2') },
      { q: t('assistant.gettingStarted.q3'), a: t('assistant.gettingStarted.a3') },
      { q: t('assistant.gettingStarted.q4'), a: t('assistant.gettingStarted.a4') },
      { q: t('assistant.gettingStarted.q5'), a: t('assistant.gettingStarted.a5') }
    ],
    dukeMechanics: [
      { q: t('assistant.dukeMechanics.q1'), a: t('assistant.dukeMechanics.a1') },
      { q: t('assistant.dukeMechanics.q2'), a: t('assistant.dukeMechanics.a2') },
      { q: t('assistant.dukeMechanics.q3'), a: t('assistant.dukeMechanics.a3') },
      { q: t('assistant.dukeMechanics.q4'), a: t('assistant.dukeMechanics.a4') },
      { q: t('assistant.dukeMechanics.q5'), a: t('assistant.dukeMechanics.a5') }
    ],
    multiplayer: [
      { q: t('assistant.multiplayer.q1'), a: t('assistant.multiplayer.a1') },
      { q: t('assistant.multiplayer.q2'), a: t('assistant.multiplayer.a2') },
      { q: t('assistant.multiplayer.q3'), a: t('assistant.multiplayer.a3') },
      { q: t('assistant.multiplayer.q4'), a: t('assistant.multiplayer.a4') },
      { q: t('assistant.multiplayer.q5'), a: t('assistant.multiplayer.a5') },
      { q: t('assistant.multiplayer.q6'), a: t('assistant.multiplayer.a6') },
      { q: t('assistant.multiplayer.q7'), a: t('assistant.multiplayer.a7') }
    ],
    customization: [
      { q: t('assistant.customization.q1'), a: t('assistant.customization.a1') },
      { q: t('assistant.customization.q2'), a: t('assistant.customization.a2') },
      { q: t('assistant.customization.q3'), a: t('assistant.customization.a3') },
      { q: t('assistant.customization.q4'), a: t('assistant.customization.a4') },
      { q: t('assistant.customization.q5'), a: t('assistant.customization.a5') },
      { q: t('assistant.customization.q6'), a: t('assistant.customization.a6') },
      { q: t('assistant.customization.q7'), a: t('assistant.customization.a7') }
    ],
    boardFeatures: [
      { q: t('assistant.boardFeatures.q1'), a: t('assistant.boardFeatures.a1') },
      { q: t('assistant.boardFeatures.q2'), a: t('assistant.boardFeatures.a2') },
      { q: t('assistant.boardFeatures.q3'), a: t('assistant.boardFeatures.a3') },
      { q: t('assistant.boardFeatures.q4'), a: t('assistant.boardFeatures.a4') },
      { q: t('assistant.boardFeatures.q5'), a: t('assistant.boardFeatures.a5') }
    ],
    skinStore: [
      { q: t('assistant.skinStore.q1'), a: t('assistant.skinStore.a1') },
      { q: t('assistant.skinStore.q2'), a: t('assistant.skinStore.a2') },
      { q: t('assistant.skinStore.q3'), a: t('assistant.skinStore.a3') },
      { q: t('assistant.skinStore.q4'), a: t('assistant.skinStore.a4') },
      { q: t('assistant.skinStore.q5'), a: t('assistant.skinStore.a5') },
      { q: t('assistant.skinStore.q6'), a: t('assistant.skinStore.a6') }
    ],
    trialPayments: [
      { q: t('assistant.trialPayments.q1'), a: t('assistant.trialPayments.a1') },
      { q: t('assistant.trialPayments.q2'), a: t('assistant.trialPayments.a2') },
      { q: t('assistant.trialPayments.q3'), a: t('assistant.trialPayments.a3') },
      { q: t('assistant.trialPayments.q4'), a: t('assistant.trialPayments.a4') },
      { q: t('assistant.trialPayments.q5'), a: t('assistant.trialPayments.a5') }
    ],
    strategyTips: [
      { q: t('assistant.strategyTips.q1'), a: t('assistant.strategyTips.a1') },
      { q: t('assistant.strategyTips.q2'), a: t('assistant.strategyTips.a2') },
      { q: t('assistant.strategyTips.q3'), a: t('assistant.strategyTips.a3') },
      { q: t('assistant.strategyTips.q4'), a: t('assistant.strategyTips.a4') },
      { q: t('assistant.strategyTips.q5'), a: t('assistant.strategyTips.a5') }
    ],
    troubleshooting: [
      { q: t('assistant.troubleshooting.q1'), a: t('assistant.troubleshooting.a1') },
      { q: t('assistant.troubleshooting.q2'), a: t('assistant.troubleshooting.a2') },
      { q: t('assistant.troubleshooting.q3'), a: t('assistant.troubleshooting.a3') },
      { q: t('assistant.troubleshooting.q4'), a: t('assistant.troubleshooting.a4') },
      { q: t('assistant.troubleshooting.q5'), a: t('assistant.troubleshooting.a5') },
      { q: t('assistant.troubleshooting.q6'), a: t('assistant.troubleshooting.a6') },
      { q: t('assistant.troubleshooting.q7'), a: t('assistant.troubleshooting.a7') },
      { q: t('assistant.troubleshooting.q8'), a: t('assistant.troubleshooting.a8') }
    ]
  };

  // Category display names
  const CATEGORY_NAMES = {
    gettingStarted: t('assistant.categories.gettingStarted'),
    dukeMechanics: t('assistant.categories.dukeMechanics'),
    multiplayer: t('assistant.categories.multiplayer'),
    customization: t('assistant.categories.customization'),
    boardFeatures: t('assistant.categories.boardFeatures'),
    skinStore: t('assistant.categories.skinStore'),
    trialPayments: t('assistant.categories.trialPayments'),
    strategyTips: t('assistant.categories.strategyTips'),
    troubleshooting: t('assistant.categories.troubleshooting')
  };

  // Build tips from translations
  const CONTEXTUAL_TIPS = [
    `💡 ${t('assistant.tips.tip1')}`,
    `⚡ ${t('assistant.tips.tip2')}`,
    `🎯 ${t('assistant.tips.tip3')}`,
    `👑 ${t('assistant.tips.tip4')}`,
    `🛡️ ${t('assistant.tips.tip5')}`,
    `🎮 ${t('assistant.tips.tip6')}`,
    `🏠 ${t('assistant.tips.tip7')}`,
    `👁️ ${t('assistant.tips.tip8')}`,
    `📩 ${t('assistant.tips.tip9')}`,
    `🚪 ${t('assistant.tips.tip10')}`,
    `⚙️ ${t('assistant.tips.tip11')}`,
    `📜 ${t('assistant.tips.tip12')}`,
    `💬 ${t('assistant.tips.tip13')}`,
    `🔑 ${t('assistant.tips.tip14')}`,
    `🎨 ${t('assistant.tips.tip15')}`,
    `⚔️ ${t('assistant.tips.tip16')}`,
    `🎲 ${t('assistant.tips.tip17')}`,
    `💎 ${t('assistant.tips.tip18')}`,
    `🔄 ${t('assistant.tips.tip19')}`,
    `📱 ${t('assistant.tips.tip20')}`,
    `📏 ${t('assistant.tips.tip21')}`,
    `🏛️ ${t('assistant.tips.tip22')}`,
    `😊 ${t('assistant.tips.tip23')}`,
    `🏰 ${t('assistant.tips.tip24')}`,
    `⚔️ ${t('assistant.tips.tip25')}`
  ];

  // Static Pro Mode content (always available regardless of language — applies to newly added feature)
  const PRO_MODE_TOPICS = [
    {
      q: 'What is Fight to be King — Professional Mode?',
      a: 'A stricter ruleset layered on top of "Fight to be King". When your King is captured, your Duke becomes restricted: it loses ALL orthogonal captures (up/down/left/right) and keeps only its diagonal captures. A pulsing orange ring appears on the restricted Duke. To reclaim the throne, you must reach your own baseline rank.'
    },
    {
      q: 'What is the difference between Pro 1 and Pro 2?',
      a: 'Pro 1 — Any move: any move (capture or non-capture) that lands the restricted Duke on its baseline transforms it into the new King. Pro 2 — Non-capture only: only a non-capturing move to the baseline transforms the Duke. Capturing on the baseline does NOT trigger transformation.'
    },
    {
      q: 'Does Pro 2 have any additional restrictions?',
      a: 'Yes. In Pro 2, the restricted Duke also cannot move onto the opponent\'s baseline rank while it remains a Duke. You must reclaim your throne on your OWN baseline — no deep incursions into enemy territory allowed.'
    },
    {
      q: 'Can I play asymmetric Pro Mode (different Pro for each side)?',
      a: 'Yes — in the New Game panel, select "Fight to be King" then tick the "Use different Pro mode for each side" checkbox. You can mix Classic, Pro 1, and Pro 2 between the two sides for deeply asymmetric duels.'
    },
    {
      q: 'Do Pro modes have their own ranked ladder?',
      a: 'Yes. There are 3 separate ELO pools: Classic, Pro 1, and Pro 2. Switch between them in the Ranked Matchmaking screen. Each pool has its own rank tier progression and leaderboard so competitive Pro play stays pure.'
    },
    {
      q: 'Does the orange ring stack with the red check ring?',
      a: 'Yes. The orange ring indicates the Duke is restricted (Pro mode + King captured), and the red ring indicates the Duke is in check. When both conditions apply, you will see both rings at the same time.'
    }
  ];

  // Include Pro Mode as an additional category
  HELP_TOPICS.proMode = PRO_MODE_TOPICS;
  CATEGORY_NAMES.proMode = '👑 Pro Mode';

  if (!isOpen) return null;

  const filteredTopics = Object.entries(HELP_TOPICS).reduce((acc, [category, items]) => {
    if (selectedCategory && category !== selectedCategory && searchQuery === '') {
      return acc;
    }
    const filtered = items.filter(item => 
      item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.a.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {});

  const showRandomTip = () => {
    const randomIndex = Math.floor(Math.random() * CONTEXTUAL_TIPS.length);
    setCurrentTip(randomIndex);
    setShowTip(true);
    setTimeout(() => setShowTip(false), 5000);
  };

  const renderContent = () => (
    <>
      <div className="assistant-search">
        <input
          type="text"
          placeholder={`🔍 ${t('assistant.searchPlaceholder')}`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
          data-testid="assistant-search"
        />
      </div>

      <div className="assistant-categories">
        {Object.keys(HELP_TOPICS).map(category => (
          <button
            key={category}
            className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => {
              setSelectedCategory(category);
              setSearchQuery('');
            }}
            data-testid={`category-${category}`}
          >
            {CATEGORY_NAMES[category]}
          </button>
        ))}
      </div>

      <div className="assistant-content">
        {Object.entries(filteredTopics).map(([category, items]) => (
          <div key={category} className="help-category">
            <h3>{CATEGORY_NAMES[category]}</h3>
            {items.map((item) => (
              <details key={item.q} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        ))}

        {Object.keys(filteredTopics).length === 0 && (
          <div className="no-results">
            <p>{t('assistant.noResults')} "{searchQuery}"</p>
            <p className="hint">{t('assistant.noResultsHint')}</p>
          </div>
        )}
      </div>

      <div className="assistant-footer">
        <button className="tip-button" onClick={showRandomTip} data-testid="random-tip-btn">
          💡 {t('assistant.randomTip')}
        </button>
      </div>

      {showTip && (
        <div className="floating-tip" data-testid="floating-tip">
          {CONTEXTUAL_TIPS[currentTip]}
        </div>
      )}
    </>
  );

  // If full page, render without modal wrapper
  if (isFullPage) {
    return (
      <div style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
        {renderContent()}
      </div>
    );
  }

  // Render as modal
  return (
    <div className="assistant-overlay" onClick={onClose}>
      <div className="assistant-modal" onClick={(e) => e.stopPropagation()}>
        <div className="assistant-header">
          <h2>🤖 {t('assistant.title')}</h2>
          <button className="close-button" onClick={onClose} data-testid="assistant-close">×</button>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default Assistant;
